import { useEffect, useState } from "react";
import {
  useClientProjectsStore,
  type ProjectRequest,
} from "../../../store/useClientProjectsStore";
import { MIcon, fmtDate } from "./shared";

/**
 * The client's own requests, and their progress through review.
 *
 * Shown above the projects table rather than inside a modal, because a request
 * the client cannot find is a request they will submit twice. It disappears
 * once every request has been converted or closed.
 *
 * Where the team has asked a question, the answer box is right here — the same
 * exchange also exists in Inbox, and either place works.
 */

// Where each status sits in the client's mind: waiting on us, waiting on them,
// settled, or finished. The tone follows that, not the status name.
const STATUS_TONE: Record<string, string> = {
  Submitted: "bg-surface-container text-on-surface-variant",
  "Under Review": "bg-primary/10 text-primary",
  "More Information Required": "bg-error-container text-error",
  "Discussing Requirements": "bg-amber-100 text-amber-700",
  Approved: "bg-tertiary-container text-tertiary",
  "Not Proceeding": "bg-surface-container text-on-surface-variant",
  "Converted to Project": "bg-tertiary-container text-tertiary",
};

// What each status actually means, in the client's terms rather than ours.
const STATUS_MEANING: Record<string, string> = {
  Submitted: "Received. The team will pick this up shortly.",
  "Under Review": "The team is reviewing what you have asked for.",
  "More Information Required": "The team needs something from you before this can go ahead.",
  "Discussing Requirements": "You and the team are working through the details.",
  Approved: "Approved. The project is being set up.",
  "Not Proceeding": "This request will not be going ahead.",
  "Converted to Project": "This is now an active project.",
};

const RequestCard = ({
  request,
  onOpenProject,
}: {
  request: ProjectRequest;
  onOpenProject: (id: string) => void;
}) => {
  const respondToRequest = useClientProjectsStore((s) => s.respondToRequest);
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);

  // Only the latest unanswered question is actionable; older ones are history.
  const pending = [...request.infoRequests].reverse().find((r) => !r.answered) || null;

  const send = async () => {
    if (!answer.trim()) return;
    setSending(true);
    const ok = await respondToRequest(request.id, answer.trim());
    setSending(false);
    if (ok) setAnswer("");
  };

  return (
    <div
      className={`p-md rounded-xl border transition-colors ${
        pending ? "border-error/30 bg-error-container/20" : "border-outline-variant"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div className="min-w-0">
          <p className="text-body-md font-bold text-on-surface">{request.projectTitle}</p>
          <p className="text-[11px] text-on-surface-variant mt-0.5">
            {request.requestNumber && `${request.requestNumber} · `}
            {request.category}
            {` · Submitted ${fmtDate(request.submittedAt)}`}
          </p>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${
            STATUS_TONE[request.status] || STATUS_TONE.Submitted
          }`}
        >
          {request.status}
        </span>
      </div>

      <p className="text-body-sm text-on-surface-variant leading-relaxed">
        {STATUS_MEANING[request.status] || ""}
      </p>

      {request.expectedDeliveryDate && (
        <p className="text-[11px] text-on-surface-variant mt-1">
          You asked for delivery by <b className="text-on-surface">{fmtDate(request.expectedDeliveryDate)}</b>
        </p>
      )}

      {/* The live question, answerable here. */}
      {pending && (
        <div className="mt-sm p-md rounded-lg bg-surface-container-lowest border border-outline-variant">
          <p className="text-[11px] font-bold uppercase tracking-wider text-error mb-1">
            The team asked
          </p>
          <p className="text-body-sm text-on-surface leading-relaxed mb-sm">{pending.question}</p>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Your answer…"
            className="w-full px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-body-sm text-on-surface outline-none focus:border-primary min-h-[72px] resize-y"
          />
          <div className="flex items-center justify-between gap-sm mt-sm flex-wrap">
            <span className="text-[11px] text-on-surface-variant">
              You can also reply in your Inbox.
            </span>
            <button
              onClick={send}
              disabled={sending || !answer.trim()}
              className="px-lg py-1.5 rounded-lg bg-primary text-on-primary text-body-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send Response"}
            </button>
          </div>
        </div>
      )}

      {/* Questions already dealt with, kept so nothing is lost. */}
      {request.infoRequests.filter((r) => r.answered).length > 0 && (
        <details className="mt-sm">
          <summary className="text-[11px] font-semibold text-on-surface-variant cursor-pointer hover:text-on-surface">
            Earlier questions ({request.infoRequests.filter((r) => r.answered).length})
          </summary>
          <ul className="mt-sm space-y-sm">
            {request.infoRequests
              .filter((r) => r.answered)
              .map((r) => (
                <li key={r.id} className="text-body-sm p-2.5 rounded-lg bg-surface-container-low">
                  <p className="text-on-surface-variant">{r.question}</p>
                  <p className="text-on-surface mt-1">{r.response}</p>
                </li>
              ))}
          </ul>
        </details>
      )}

      {request.project && (
        <button
          onClick={() => onOpenProject(request.project as string)}
          className="mt-sm inline-flex items-center gap-1 text-body-sm font-bold text-primary hover:underline"
        >
          Open the project
          <MIcon name="arrow_forward" className="text-[16px]" />
        </button>
      )}
    </div>
  );
};

const MyRequestsPanel = ({ onOpenProject }: { onOpenProject: (id: string) => void }) => {
  const requests = useClientProjectsStore((s) => s.requests);
  const awaitingYou = useClientProjectsStore((s) => s.requestsAwaitingYou);
  const fetchRequests = useClientProjectsStore((s) => s.fetchRequests);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A request that has become a project, or was closed, belongs in the projects
  // table or in nobody's way — not here.
  const live = requests.filter((r) => r.isOpen);
  if (!live.length) return null;

  const shown = expanded ? live : live.slice(0, 2);

  return (
    <div className="mb-xl">
      <div className="flex items-center gap-2 mb-sm">
        <MIcon name="pending_actions" className="text-primary text-[18px]" />
        <h2 className="text-[12px] font-bold uppercase tracking-wider text-on-surface">
          Your Requests
        </h2>
        <span className="text-[11px] text-on-surface-variant font-medium">
          · {live.length} in review
          {awaitingYou > 0 && (
            <span className="text-error font-bold"> · {awaitingYou} waiting on you</span>
          )}
        </span>
      </div>

      <div className="space-y-sm">
        {shown.map((r) => (
          <RequestCard key={r.id} request={r} onOpenProject={onOpenProject} />
        ))}
      </div>

      {live.length > 2 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-sm text-body-sm font-bold text-primary hover:underline"
        >
          {expanded ? "Show fewer" : `Show all ${live.length}`}
        </button>
      )}
    </div>
  );
};

export default MyRequestsPanel;
